import * as React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { useI18n } from "../../i18n/I18nProvider";
import { useWorkshopBlocker, FLOW } from "../../logic/useWorkshopBlocker";


export default function ScenarioEmergency(props: {handlePageChange: (page: string) => void}) {
  const { handlePageChange } = props;
  const blocker = useWorkshopBlocker();
  const { lang } = useI18n();
  const isDE = lang === "de";
  const [notice, setNotice] = React.useState<string | null>(null);

  /* ---------------- NAVIGATION ---------------- */

  const myIndex = FLOW.indexOf("ScenarioEmergency");
  const blockerIndex = FLOW.indexOf(blocker);
  const canContinue = blockerIndex > myIndex;
  
  function handleContinue() {
    if (canContinue) {
      handlePageChange(FLOW[myIndex + 1]);
      return;
    }
    setNotice("ENG / DE");
  }

  /* ---------------- UI ---------------- */

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
        backgroundImage: `
          linear-gradient(
            rgba(8, 8, 8, 0.78),
            rgba(8, 8, 8, 0.82)
          ),
          url("/scenarioBgs/emergency.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: { xs: "scroll", md: "fixed" },
      }}
    >
      <Paper
        sx={{
          width: "min(900px, 90vw)",
          borderRadius: 5,
          p: { xs: 4, sm: 6, md: 7 },

          border: "1px solid rgba(255,255,255,0.14)",
          background:
            "linear-gradient(180deg, rgba(49, 32, 32, 0.95), rgba(34, 21, 21, 0.94))",

          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
          textAlign: "center",
        }}
      >
        <Stack spacing={5} alignItems="center">

          {/* TITLE */}
          <Typography
            sx={{
              color: "#ff4d4d",
              fontSize: { xs: "1.4rem", sm: "1.8rem" },
              fontWeight: 800,
              letterSpacing: "0.12em",
            }}
          >
            🔥 {isDE ? "NOTFALL" : "EMERGENCY"}
          </Typography>

          {/* SCENARIO */}
          <Typography
            sx={{
              color: "#fff",
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
              fontWeight: 700,
              lineHeight: 1.4,
              maxWidth: 700,
            }}
          >
            {isDE
              ? "Eine Person ist bewusstlos und reagiert nicht. Du bist die einzige Person vor Ort."
              : "A person is unconscious and not responding. You are the only person present."}
          </Typography>

          {/* DURATION */}
          <Typography
            sx={{
              color: "#ff8a8a",
              fontSize: { xs: "1rem", sm: "1.2rem" },
              fontWeight: 700,
            }}
          >
            {isDE
              ? "👉 Was tust du jetzt?"
              : "👉 What do you do now?"}
          </Typography>

          {/* TASK TITLE */}
          <Typography
            sx={{
              color: "#fff",
              fontSize: { xs: "1.1rem", sm: "1.3rem" },
              fontWeight: 800,
              letterSpacing: "0.08em",
              mt: 2,
            }}
          >
            {isDE ? "AUFGABE" : "TASK"}
          </Typography>

          {/* TASK TEXT */}
          <Typography
            sx={{
              color: "rgba(255,255,255,0.85)",
              fontSize: { xs: "1rem", sm: "1.15rem" },
              maxWidth: 700,
              lineHeight: 1.5,
            }}
          >
            {isDE
              ? "Ordne die folgenden Schritte in die richtige Reihenfolge. Denke an Sicherheit, schnelle Hilfe und klare Prioritäten!"
              : "Arrange the following steps in the correct order. Think about safety, getting help quickly, and clear priorities!"}
          </Typography>

          {/* LIMIT */}
          <Typography
            sx={{
              color: "#00ff88",
              fontSize: { xs: "1rem", sm: "1.2rem" },
              fontWeight: 800,
            }}
          >
            {isDE
              ? "10 Karten von erster Handlung bis letzte Maßnahme"
              : "10 cards from first action to final step"}
          </Typography>

          <Button
            variant="contained"
            onClick={handleContinue}
            sx={{
              mt: 1,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 800,
              px: 3,
              bgcolor: canContinue ? "#ffffff" : "rgba(255,255,255,0.35)",
              color: canContinue ? "#111827" : "rgba(255,255,255,0.8)",
              "&:hover": {
                bgcolor: canContinue ? "#f3f4f6" : "rgba(255,255,255,0.35)",
              },
            }}
          >
            {isDE ? "Weiter" : "Continue"}
          </Button>
        </Stack>
      </Paper>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setNotice(null)} severity="info" sx={{ width: "100%", fontWeight: 700 }}>
          {isDE ? "Die Workshop-Leitung wird diesen Schritt freigeben, oder bitte beantworte die Frage vollständig und sende sie zuerst ab." : "The workshop holder will enable this step, or please complete and submit your answer first."}
        </Alert>
      </Snackbar>
    </Box>
  );
}